/**
 * PII (Personally Identifiable Information) Filter for Sentry
 *
 * This module provides utilities to scrub sensitive data from Sentry events
 * while preserving safe ID fields that help with debugging.
 */

const REDACTED = '[REDACTED]';

/**
 * PII field patterns that should be filtered from Sentry logs
 * These fields contain personally identifiable or sensitive information
 */
const PII_FIELD_PATTERNS = [
  // Personal Information
  /^firstName$/i,
  /^lastName$/i,
  /^middleName$/i,
  /^fullName$/i,
  /^displayName$/i,
  /^name$/i, // Generic name field

  // Contact Information
  /^email$/i,
  /^phoneNumber$/i,
  /^cellPhoneNumber$/i,
  /^businessPhoneNumber$/i,
  /^challengePhone$/i,
  /^phone$/i,

  // Date of Birth
  /^dob$/i,
  /^dateOfBirth$/i,
  /^birthDate$/i,

  // Social Security & Tax IDs
  /^ssn$/i,
  /^socialSecurity$/i,
  /^confirmSsn$/i,
  /^tin$/i,
  /^confirmTin$/i,

  // Address Fields
  /^address$/i,
  /^currentAddress$/i,
  /^mailingAddress$/i,
  /^ownerAddress$/i,
  /^bankAddress$/i,
  /^recipientAddress$/i,
  /Line1$/i,
  /Line2$/i,
  /City$/i,
  /State$/i,
  /PostalCode$/i,
  /CountryCode$/i,
  /^zip$/i,
  /^city$/i,
  /^state$/i,
  /^country$/i,

  // Financial Account Information
  /^accountNumber$/i,
  /^confirmAccountNumber$/i,
  /^routingNumber$/i,
  /^confirmRoutingNumber$/i,
  /^cardNumber$/i,

  // Wire & Recipient Info
  /^recipientName$/i,
  /^recipientNickName$/i,
  /^accountName$/i,

  // Authentication & Security
  /password/i,
  /^token$/i,
  /Token$/i,
  /^session$/i,
  /^apiKey$/i,
  /^secret$/i,
  /^otp$/i,
  /^otp_code$/i,
  /^sms_mfa_code$/i,
  /^activation_code$/i,
  /^set_password_code$/i,
  /^setPasswordCode$/i,

  // Document URLs (may contain sensitive paths)
  /^presignedUrl$/i,
  /^imageFrontUrl$/i,
  /^imageBackUrl$/i,
  /^proofsOfOwnership$/i,
  /^avatarUrl$/i,
  /^avatar$/i,

  // Notes & Free Text (may contain PII)
  /^note$/i,
  /^notes$/i,
  /^instructions$/i,
  /^memo$/i,
  /^dueDiligenceNotes$/i,
  /^declinedReason$/i,
  /^bio$/i,

  // Business Names (when tied to individuals)
  /^legalBusinessName$/i,
  /^dbaName$/i,
];

/**
 * Safe ID fields that should be preserved for debugging
 * These help identify entities without exposing PII
 */
const SAFE_ID_PATTERNS = [
  // Generic IDs
  /^uuid$/i,
  /^id$/i,
  /Id$/,

  // User & Customer
  /^userId$/i,
  /^customerId$/i,
  /^consumerId$/i,
  /^ownerId$/i,
  /^memberId$/i,

  // Business & Company
  /^businessId$/i,
  /^companyId$/i,
  /^teammateId$/i,
  /^advisorId$/i,
  /^staffId$/i,

  // Account & Transaction
  /^bankAccountId$/i,
  /^accountId$/i,
  /^transactionId$/i,
  /^applicationId$/i,
  /^shortId$/i,
  /^feeForId$/i,

  // External IDs
  /^extId$/i,
  /^externalId$/i,
  /^tpDepositSweepId$/i,
  /^treasuryPrimeLogoId$/i,
  /^zendeskId$/i,
  /^zendeskUserId$/i,
  /^zendeskUserAgentId$/i,
  /^zendeskOrganizationId$/i,
  /^zendeskChannelIosId$/i,
  /^zendeskChannelAndroidId$/i,
  /^debitCardProgramId$/i,
  /^plaidId$/i,

  // Request & Review
  /^idempotencyKey$/i,
  /^requestId$/i,
  /^reviewId$/i,
  /^recipientId$/i,
  /^documentId$/i,
  /^statementId$/i,

  // Relationship IDs
  /^relationshipManagerId$/i,
  /^primaryOwnerId$/i,
  /^createdBy$/i,
  /^updatedBy$/i,
  /^approvedBy$/i,
  /^deletedBy$/i,

  // Metadata
  /^brandingThemeId$/i,
  /^logoId$/i,

  // Masked/Last4 (safe)
  /Last4$/i,
  /last4Digits$/i,
];

/**
 * Check if a field name matches PII patterns
 */
function isPIIField(fieldName: string): boolean {
  return PII_FIELD_PATTERNS.some(pattern => pattern.test(fieldName));
}

/**
 * Check if a field name is a safe ID field
 */
function isSafeIDField(fieldName: string): boolean {
  return SAFE_ID_PATTERNS.some(pattern => pattern.test(fieldName));
}

/**
 * Recursively scrub PII from an object
 */
export function scrubPII(data: any, maxDepth = 10, currentDepth = 0): any {
  // Prevent infinite recursion
  if (currentDepth > maxDepth) {
    return '[MAX_DEPTH_EXCEEDED]';
  }

  // Handle null/undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle primitive types
  if (typeof data !== 'object') {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => scrubPII(item, maxDepth, currentDepth + 1));
  }

  // Handle objects
  const scrubbedObject: any = {};

  for (const [key, value] of Object.entries(data)) {
    // Always preserve safe ID fields
    if (isSafeIDField(key)) {
      scrubbedObject[key] = value;
      continue;
    }

    // Redact PII fields
    if (isPIIField(key)) {
      scrubbedObject[key] = REDACTED;
      continue;
    }

    // Recursively scrub nested objects
    if (value !== null && typeof value === 'object') {
      scrubbedObject[key] = scrubPII(value, maxDepth, currentDepth + 1);
    } else {
      scrubbedObject[key] = value;
    }
  }

  return scrubbedObject;
}

/**
 * Scrub PII from URLs in the event
 * Removes query parameters and path segments that might contain sensitive data
 */
export function scrubURLs(url: string): string {
  try {
    const urlObj = new URL(url);

    // Remove all query parameters as they might contain sensitive data
    urlObj.search = '';

    // Remove hash fragments
    urlObj.hash = '';

    // Keep the pathname but check for common sensitive patterns
    const pathname = urlObj.pathname;

    // Replace UUIDs and long IDs with placeholders while preserving structure
    const scrubbedPath = pathname
      .replace(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
        '[UUID]',
      )
      .replace(/\/[0-9a-f]{20,}/gi, '/[ID]');

    urlObj.pathname = scrubbedPath;

    return urlObj.toString();
  } catch {
    // If URL parsing fails, redact the entire URL
    return '[INVALID_URL]';
  }
}

/**
 * Scrub sensitive data from error messages
 * Removes common patterns that might leak PII
 */
export function scrubErrorMessage(message: string): string {
  if (!message || typeof message !== 'string') {
    return message;
  }

  // Remove email addresses
  let scrubbed = message.replace(
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    '[EMAIL]',
  );

  // Remove SSN patterns (check before other number patterns)
  scrubbed = scrubbed.replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[SSN]');

  // Remove account number patterns (9-17 digits) - check before phone numbers
  scrubbed = scrubbed.replace(/\b\d{9,17}\b/g, '[ACCOUNT_NUMBER]');

  // Remove phone numbers (various formats) - check after account numbers
  scrubbed = scrubbed.replace(
    /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,
    '[PHONE]',
  );

  return scrubbed;
}

/**
 * Main filter function to scrub all PII from a Sentry event
 */
export function filterSentryEvent(event: any): any {
  // Scrub request data
  if (event.request) {
    if (event.request.data) {
      event.request.data = scrubPII(event.request.data);
    }

    if (event.request.query_string) {
      // Completely remove query strings as they often contain sensitive data
      event.request.query_string = '[REDACTED]';
    }

    if (event.request.url) {
      event.request.url = scrubURLs(event.request.url);
    }

    // Scrub cookies and headers that might contain tokens
    if (event.request.cookies) {
      event.request.cookies = '[REDACTED]';
    }

    if (event.request.headers) {
      const safeHeaders = ['content-type', 'user-agent', 'referer'];
      const scrubbedHeaders: any = {};

      for (const [key, value] of Object.entries(event.request.headers)) {
        if (safeHeaders.includes(key.toLowerCase())) {
          scrubbedHeaders[key] = value;
        } else {
          scrubbedHeaders[key] = REDACTED;
        }
      }

      event.request.headers = scrubbedHeaders;
    }
  }

  // Scrub contexts (user, extra, etc.)
  if (event.contexts) {
    for (const [contextKey, contextValue] of Object.entries(event.contexts)) {
      if (contextValue && typeof contextValue === 'object') {
        event.contexts[contextKey] = scrubPII(contextValue);
      }
    }
  }

  // Scrub extra data
  if (event.extra) {
    event.extra = scrubPII(event.extra);
  }

  // Scrub user info (keep only IDs)
  if (event.user) {
    const scrubbedUser: any = {};

    // Keep safe ID fields
    if (event.user.id) scrubbedUser.id = event.user.id;
    if (event.user.userId) scrubbedUser.userId = event.user.userId;
    if (event.user.customerId) scrubbedUser.customerId = event.user.customerId;

    // Redact everything else
    if (event.user.email) scrubbedUser.email = REDACTED;
    if (event.user.username) scrubbedUser.username = REDACTED;
    if (event.user.ip_address) scrubbedUser.ip_address = REDACTED;

    event.user = scrubbedUser;
  }

  // Scrub breadcrumbs
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb: any) => {
      if (breadcrumb.data) {
        breadcrumb.data = scrubPII(breadcrumb.data);
      }

      if (breadcrumb.message) {
        breadcrumb.message = scrubErrorMessage(breadcrumb.message);
      }

      return breadcrumb;
    });
  }

  // Scrub exception messages
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((exception: any) => {
      if (exception.value) {
        exception.value = scrubErrorMessage(exception.value);
      }
      return exception;
    });
  }

  // Scrub message
  if (event.message) {
    event.message = scrubErrorMessage(event.message);
  }

  return event;
}
